import { SelectChangeEvent, Tooltip } from '@mui/material';
import { UnitSystem } from '@trackman/baseball-shared-components';
import { Select } from '@trackman/web-shared-components';
import { InputWithAdornment } from 'components/InputWIthAdorment/InputWIthAdornment';
import { ServiceTypes } from 'constants/ServiceTypes';
import { ReactComponent as ResetIcon } from 'images/resetIcon.svg';
import { LeagueStrikeZoneEnum, StrikeZoneDefinitionEnum, StrikeZoneDict } from 'interfaces/session/IStrikeZoneDefinition';
import { IApiLeagueStrikeZones } from 'interfaces/team/IApiScheduleResponse';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useInjection } from 'services/ioc/ioc.react';
import { LineupStore } from 'stores/LineupStore';
import { HomeplateSection } from './HomeplateSection/HomeplateSection';
import {
  Dimensions,
  Divider,
  ResetWrapper,
  SelectStrikeZoneWrapper,
  StrikeZone,
  StrikeZoneSelect,
  StrikeZoneValues,
  StyledButton,
  StyledRadio,
  StyledRadioGroup,
  Title,
} from './SelectStrikeZoneStyles';
import { StrikeZoneCalculation } from './StrikeZoneCalculation/StrikeZoneCalculation';
import StrikeZoneImage from './StrikeZoneImage';

interface IProps {
  isPrivateGame: boolean;
}

const SelectStrikeZone: React.FC<IProps> = observer((props) => {
  const { isPrivateGame } = props;
  const lineupStore = useInjection<LineupStore>(ServiceTypes.LineupStore);
  const [selectedStrikeZone, setSelectedStrikeZone] = useState<keyof IApiLeagueStrikeZones>();

  const onUnitSystemChange = (event: React.ChangeEvent<HTMLInputElement>, value: UnitSystem) => {
    lineupStore.setMetrics(value);
    lineupStore.recalculateStrikeZoneValues(value);
  };

  const onStrikeZoneChange = (event: SelectChangeEvent<React.ReactText>) => {
    setSelectedStrikeZone(() => {
      switch (event.target.value as StrikeZoneDefinitionEnum) {
        case StrikeZoneDefinitionEnum.Static:
          return LeagueStrikeZoneEnum.StaticStrikeZone;

        case StrikeZoneDefinitionEnum.HeightBased:
          return LeagueStrikeZoneEnum.HeightBasedStrikeZone;

        case StrikeZoneDefinitionEnum.TopAndBottomBased:
          return LeagueStrikeZoneEnum.TopAndBottomStrikeZone;
      }
    });

    lineupStore.overrideStrikeZoneDefinition('activeStrikeZone', event.target.value);
  };

  useEffect(() => {
    setSelectedStrikeZone(() => {
      switch (lineupStore.league.activeStrikeZone) {
        case StrikeZoneDefinitionEnum.Static:
          return LeagueStrikeZoneEnum.StaticStrikeZone;
        case StrikeZoneDefinitionEnum.HeightBased:
          return LeagueStrikeZoneEnum.HeightBasedStrikeZone;
        case StrikeZoneDefinitionEnum.TopAndBottomBased:
          return LeagueStrikeZoneEnum.TopAndBottomStrikeZone;
      }
    });
  }, []);

  useEffect(() => {
    if (!lineupStore.league.activeStrikeZone) {
      setSelectedStrikeZone(LeagueStrikeZoneEnum.StaticStrikeZone);
    }
  }, []);

  const getStrikeZoneOptions = () => {
    return Object.keys(StrikeZoneDefinitionEnum).map((strikeZone) => ({
      label: StrikeZoneDict[strikeZone],
      value: strikeZone,
    }));
  };

  return (
    <SelectStrikeZoneWrapper>
      <StrikeZoneSelect>
        <Title>{lineupStore.league.name} league's strike zone</Title>
        <Divider />
        <Dimensions>
          <StyledRadioGroup name='strikezone' value={lineupStore.metrics} onChange={onUnitSystemChange} data-testid='metrics-radio-group'>
            <StyledRadio
              value={UnitSystem.Imperial}
              label={UnitSystem.Imperial}
              withoutBorder
              checked={lineupStore.metrics === UnitSystem.Imperial}
              data-testid='imperial-radio-btn'
            />
            <StyledRadio
              value={UnitSystem.Metric}
              label={UnitSystem.Metric}
              withoutBorder
              checked={lineupStore.metrics === UnitSystem.Metric}
              data-testid='metric-radio-btn'
            />
          </StyledRadioGroup>
        </Dimensions>
      </StrikeZoneSelect>
      <HomeplateSection />
      <StrikeZone>
        <Select
          label='Strike zone type'
          selectOptions={getStrikeZoneOptions()}
          width={416}
          value={lineupStore.overridedStrikeZone.activeStrikeZone}
          defaultValue={lineupStore.league.activeStrikeZone || lineupStore.overridedStrikeZone.activeStrikeZone}
          onChange={onStrikeZoneChange}
          data-testid='strike-zone-type-select'
        />
        <StrikeZoneValues>
          {selectedStrikeZone === LeagueStrikeZoneEnum.StaticStrikeZone && (
            <>
              <InputWithAdornment
                metrics={lineupStore.metrics}
                onChange={(value) => lineupStore.overrideStrikeZoneDefinition('staticStrikeZoneTop', value)}
                label='Top of SZ'
                value={lineupStore.overridedStrikeZone?.staticStrikeZoneTop}
                adornmentValue={lineupStore.metrics === UnitSystem.Imperial ? 'in' : 'cm'}
                defaultValue={lineupStore.defaultStrikeZone?.staticStrikeZoneTop}
                data-testid='static-strike-zone-top'
              />
              <InputWithAdornment
                metrics={lineupStore.metrics}
                onChange={(value) => lineupStore.overrideStrikeZoneDefinition('staticStrikeZoneBottom', value)}
                label='Bottom of SZ'
                value={lineupStore.overridedStrikeZone?.staticStrikeZoneBottom}
                adornmentValue={lineupStore.metrics === UnitSystem.Imperial ? 'in' : 'cm'}
                defaultValue={lineupStore.defaultStrikeZone?.staticStrikeZoneBottom}
                data-testid='static-strike-zone-bottom'
              />
              <InputWithAdornment
                metrics={lineupStore.metrics}
                onChange={(value) => lineupStore.overrideStrikeZoneDefinition('staticStrikeZoneWidth', value)}
                label='Width of SZ'
                value={lineupStore.overridedStrikeZone?.staticStrikeZoneWidth}
                adornmentValue={lineupStore.metrics === UnitSystem.Imperial ? 'in' : 'cm'}
                defaultValue={lineupStore.defaultStrikeZone?.staticStrikeZoneWidth}
                data-testid='static-strike-zone-width'
              />
            </>
          )}
          {selectedStrikeZone === LeagueStrikeZoneEnum.HeightBasedStrikeZone && (
            <>
              <InputWithAdornment
                metrics={lineupStore.metrics}
                onChange={(value) => lineupStore.overrideStrikeZoneDefinition('heightStrikeZoneTopPercentage', value)}
                label='Top of SZ'
                value={lineupStore.overridedStrikeZone?.heightStrikeZoneTopPercentage}
                adornmentValue={'%'}
                defaultValue={lineupStore.defaultStrikeZone?.heightStrikeZoneTopPercentage}
                data-testid='height-strike-zone-top-percentage'
              />
              <InputWithAdornment
                metrics={lineupStore.metrics}
                onChange={(value) => lineupStore.overrideStrikeZoneDefinition('heightStrikeZoneBottomPercentage', value)}
                label='Bottom of SZ'
                value={lineupStore.overridedStrikeZone?.heightStrikeZoneBottomPercentage}
                adornmentValue={'%'}
                defaultValue={lineupStore.defaultStrikeZone?.heightStrikeZoneBottomPercentage}
                data-testid='height-strike-zone-bottom-percentage'
              />
              <InputWithAdornment
                metrics={lineupStore.metrics}
                onChange={(value) => lineupStore.overrideStrikeZoneDefinition('heightStrikeZoneWidth', value)}
                label='Width of SZ'
                value={lineupStore.overridedStrikeZone?.heightStrikeZoneWidth}
                adornmentValue={lineupStore.metrics === UnitSystem.Imperial ? 'in' : 'cm'}
                defaultValue={lineupStore.defaultStrikeZone?.heightStrikeZoneWidth}
                data-testid='height-strike-zone-width'
              />
            </>
          )}
          {selectedStrikeZone === LeagueStrikeZoneEnum.TopAndBottomStrikeZone && (
            <>
              <InputWithAdornment
                metrics={lineupStore.metrics}
                onChange={(value) => lineupStore.overrideStrikeZoneDefinition('topBottomStrikeZoneWidth', value)}
                label='Width of SZ'
                value={lineupStore.overridedStrikeZone?.topBottomStrikeZoneWidth}
                adornmentValue={lineupStore.metrics === UnitSystem.Imperial ? 'in' : 'cm'}
                defaultValue={lineupStore.defaultStrikeZone?.topBottomStrikeZoneWidth}
                data-testid='top-bottom-strike-zone-width'
              />
            </>
          )}
          <InputWithAdornment
            metrics={lineupStore.metrics}
            onChange={(value) => lineupStore.overrideStrikeZoneDefinition('ballDiameter', value)}
            label='Ball diameter'
            value={lineupStore.overridedStrikeZone?.ballDiameter || 0}
            adornmentValue={lineupStore.metrics === UnitSystem.Imperial ? 'in' : 'cm'}
            defaultValue={lineupStore.defaultStrikeZone?.ballDiameter}
            data-testid='ball-diameter'
          />

          <Tooltip
            title={
              <>
                <div>Reset SZ dimensions to</div>
                <div>{lineupStore.league.name}'s default</div>
              </>
            }
            arrow
            placement='bottom-end'
          >
            <ResetWrapper>
              <StyledButton
                intent='tertiary'
                label=''
                startIcon={<ResetIcon />}
                onClick={() => lineupStore.revertStrikeZoneChanges()}
                disabled={!lineupStore.isAnyStrikeZoneDifference}
                data-testid='strike-zone-reset-btn'
              />
            </ResetWrapper>
          </Tooltip>
        </StrikeZoneValues>
        <StrikeZoneImage selectedStrikeZone={selectedStrikeZone} isPrivateGame={isPrivateGame} />
      </StrikeZone>
      <StrikeZoneCalculation />
    </SelectStrikeZoneWrapper>
  );
});

export default SelectStrikeZone;
